import axios from 'axios'
import type { Dispatch, SetStateAction } from 'react'
import { useEffect, useState } from 'react'
import { DEFAULT_SELLER_NUMBER } from 'src/constants'

type CacheID = string

const CACHE: Record<CacheID, MulticurrencyPrices[]> = {}

interface GetUsdRatesResponse {
  value: number
}

const getUsdMulticurrencyPrices = async (
  setMulticurrencyPrices: Dispatch<SetStateAction<MulticurrencyPrices[] | []>>,
  sellerId: string,
  skuId: string,
  prices: { listPrice: number; sellingPrice: number }
) => {
  const cacheID = `${sellerId}-${skuId}`

  if (CACHE[cacheID] !== undefined) {
    setMulticurrencyPrices(CACHE[cacheID])

    return
  }

  try {
    const { data } = await axios.get<GetUsdRatesResponse>('/api/getUsdRates')

    if (!data?.value) throw new Error('Invalid USD rate')

    const usdRate = Number(data.value)

    const result = [
      {
        value: Math.round((prices.listPrice * 100) / usdRate).toFixed(2),
        discountValue:
          prices.sellingPrice !== prices.listPrice
            ? Math.round((prices.sellingPrice * 100) / usdRate).toFixed(2)
            : 'NaN',
      },
    ]

    CACHE[cacheID] = result
    setMulticurrencyPrices(result)
  } catch (err) {
    console.error('Error fetching USD rate:', err)
    setMulticurrencyPrices([])
  }
}

export const useGetMulticurrencyPrices = (
  sellerId: string,
  skuId: string,
  hasMulticurrencyPrice: boolean,
  prices: { listPrice: number; sellingPrice: number }
) => {
  const [loading, setLoading] = useState(false)
  const [multicurrencyPrices, setMulticurrencyPrices] = useState<
    MulticurrencyPrices[] | []
  >([])

  useEffect(() => {
    if (hasMulticurrencyPrice) {
      setLoading(true)
      getUsdMulticurrencyPrices(
        setMulticurrencyPrices,
        sellerId,
        skuId,
        prices
      ).finally(() => setLoading(false))
    }
  }, [
    sellerId,
    skuId,
    hasMulticurrencyPrice,
    prices.listPrice,
    prices.sellingPrice,
  ])

  const lastIndex = multicurrencyPrices.length - 1

  const multicurrecySellingPrice =
    multicurrencyPrices[lastIndex]?.discountValue === 'NaN'
      ? Number(multicurrencyPrices[lastIndex]?.value)
      : Number(multicurrencyPrices[lastIndex]?.discountValue)

  const multicurrencyListPrice =
    multicurrencyPrices[lastIndex]?.discountValue === 'NaN'
      ? null
      : Number(multicurrencyPrices[lastIndex]?.value)

  return {
    multicurrecySellingPrice,
    multicurrencyListPrice,
    loading,
  }
}
